/**
 * The locale catalog.
 *
 * TABITA ships ten locales at launch, chosen to cover the largest global
 * Christian populations. Two of them — Arabic and Urdu — are right-to-left,
 * which is why direction is modelled as data from the first commit rather than
 * retrofitted: RTL cannot be added later without revisiting every layout.
 */
export type TextDirection = 'ltr' | 'rtl';

export interface LocaleDefinition {
  /** BCP 47 language tag. */
  readonly code: string;
  /** English name, for administrator-facing lists. */
  readonly name: string;
  /** The name in its own script — a Bengali speaker looks for "বাংলা". */
  readonly nativeName: string;
  readonly direction: TextDirection;
  /** Default currency for tenants created in this locale. Overridable. */
  readonly defaultCurrency: string;
  /** Fallback chain, tried in order before English. */
  readonly fallbacks: readonly string[];
}

export const SUPPORTED_LOCALES: readonly LocaleDefinition[] = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    direction: 'ltr',
    defaultCurrency: 'USD',
    fallbacks: [],
  },
  {
    code: 'zh-CN',
    name: 'Chinese (Simplified)',
    nativeName: '简体中文',
    direction: 'ltr',
    defaultCurrency: 'CNY',
    fallbacks: ['en'],
  },
  {
    code: 'hi',
    name: 'Hindi',
    nativeName: 'हिन्दी',
    direction: 'ltr',
    defaultCurrency: 'INR',
    fallbacks: ['en'],
  },
  {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    direction: 'ltr',
    defaultCurrency: 'USD',
    fallbacks: ['en'],
  },
  {
    code: 'ar',
    name: 'Arabic',
    nativeName: 'العربية',
    direction: 'rtl',
    defaultCurrency: 'USD',
    fallbacks: ['en'],
  },
  {
    code: 'fr',
    name: 'French',
    nativeName: 'Français',
    direction: 'ltr',
    defaultCurrency: 'EUR',
    fallbacks: ['en'],
  },
  {
    code: 'bn',
    name: 'Bengali',
    nativeName: 'বাংলা',
    direction: 'ltr',
    defaultCurrency: 'BDT',
    fallbacks: ['en'],
  },
  {
    code: 'pt-BR',
    name: 'Portuguese (Brazil)',
    nativeName: 'Português',
    direction: 'ltr',
    defaultCurrency: 'BRL',
    fallbacks: ['en'],
  },
  {
    code: 'ru',
    name: 'Russian',
    nativeName: 'Русский',
    direction: 'ltr',
    defaultCurrency: 'RUB',
    fallbacks: ['en'],
  },
  {
    code: 'ur',
    name: 'Urdu',
    nativeName: 'اردو',
    direction: 'rtl',
    defaultCurrency: 'PKR',
    fallbacks: ['en'],
  },
];

export const DEFAULT_LOCALE = 'en';

export const SUPPORTED_LOCALE_CODES: readonly string[] = SUPPORTED_LOCALES.map((l) => l.code);

const BY_CODE = new Map(SUPPORTED_LOCALES.map((locale) => [locale.code, locale]));

export function findLocale(code: string): LocaleDefinition | undefined {
  return BY_CODE.get(code);
}

export function isSupportedLocale(code: string): boolean {
  return BY_CODE.has(code);
}

export function directionFor(code: string): TextDirection {
  return BY_CODE.get(code)?.direction ?? 'ltr';
}

export function isRtl(code: string): boolean {
  return directionFor(code) === 'rtl';
}

/**
 * Resolves a request to a supported locale.
 *
 * Order of precedence: the user's explicit choice, then the tenant default,
 * then the browser's Accept-Language, then English. A user who has chosen
 * Urdu must never be shown English because their laptop is set to en-US.
 */
export function resolveLocale(input: {
  userLocale?: string | null;
  tenantLocale?: string | null;
  acceptLanguage?: string | null;
}): LocaleDefinition {
  const candidates = [
    input.userLocale,
    input.tenantLocale,
    ...parseAcceptLanguage(input.acceptLanguage ?? ''),
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    const exact = BY_CODE.get(candidate);
    if (exact) return exact;

    // `pt` should reach `pt-BR`, and `zh-Hans-CN` should reach `zh-CN`.
    const base = candidate.split('-')[0];
    const regional = SUPPORTED_LOCALES.find((locale) => locale.code.split('-')[0] === base);
    if (regional) return regional;
  }

  return BY_CODE.get(DEFAULT_LOCALE)!;
}

/** Parses an Accept-Language header into tags ordered by descending q-value. */
export function parseAcceptLanguage(header: string): string[] {
  if (!header.trim()) return [];

  return header
    .split(',')
    .map((entry) => {
      const [tag, ...params] = entry.trim().split(';');
      const quality = params.map((param) => /^q=([\d.]+)$/.exec(param.trim())?.[1]).find(Boolean);
      return { tag: (tag ?? '').trim(), quality: quality ? Number(quality) : 1 };
    })
    .filter((entry) => entry.tag.length > 0 && entry.tag !== '*')
    .sort((a, b) => b.quality - a.quality)
    .map((entry) => entry.tag);
}

/**
 * The chain to try when a key is missing, ending at English.
 *
 * A missing translation must degrade to a readable string, never to a raw key
 * shown to a congregation member.
 */
export function fallbackChain(code: string): string[] {
  const locale = BY_CODE.get(code);
  if (!locale) return [DEFAULT_LOCALE];
  return [code, ...locale.fallbacks, DEFAULT_LOCALE].filter(
    (value, index, all) => all.indexOf(value) === index,
  );
}
